import React, { useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import LazyLoad from "react-lazyload";
import south from "../../assets/images/restore/southRestore.webp";
import north from "../../assets/images/restore/northRestore.webp";
import CTASection from "../../components/CTASection";
import DynamicEvents from "../../components/DynamicEvents";

const ReStore = () => {
  useEffect(() => {
    // Ensure the ReSupply widget loads
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://widget.resupplyapp.com/prod/loader.js";
    script.setAttribute("widget_code", "9b98287dd80a486f3d28743ede5dcfbd");
    script.onload = () => console.log("ReSupply widget loaded");
    document.body.appendChild(script);
  }, []);

  const handleResupplyClick = () => {
    if (window.ResupplyWidget) {
      window.ResupplyWidget.open();
    } else {
      console.error("ReSupply Widget not loaded yet.");
    }
  };
  return (
    <>
      <Helmet>
        <title>ReStore - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Visit Habitat for Humanity Buffalo ReStores for incredible deals on new or gently-used furniture, appliances, home decor, and building materials. Check our locations and hours."
        />
      </Helmet>
      <Container>
        <h2 className="section-header primary">
          Habitat Buffalo ReStore Locations and Hours
        </h2>
        <Paragraph>
          Come visit us at our home improvement thrift stores! We have new
          inventory every day that will inspire you to create the home of your
          dreams. Find incredible deals on new or gently-used furniture,
          appliances, home decor, building materials, doors, windows, flooring,
          kitchen cabinets, and so much more at our Habitat for Humanity Buffalo
          ReStores. We have two locations to serve you, locations and hours are
          below for your reference.
        </Paragraph>

        <StoreHoursSection>
          <StoreLocation>
            <LazyLoad height={200} offset={100}>
              <StoreImage src={south} alt="South Buffalo ReStore" />
            </LazyLoad>
            <h3 className="project-title">South Buffalo ReStore</h3>
            <p>
              1675 South Park Ave, Buffalo, NY 14220
              <br />
              Phone: (716) 204-0740 ext. 302
              <br />
              <hr />
              <strong>Hours of Operation:</strong>
              <br />
              Tuesday - Saturday 10:00am to 5:00pm
              <br />
              Closed Sunday and Monday
            </p>
            <ButtonContainer>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=1675+South+Park+Ave,+Buffalo,+NY+14220"
                target="_blank"
                rel="noopener noreferrer"
                className="button secondary"
              >
                GET DIRECTIONS
              </a>
            </ButtonContainer>
          </StoreLocation>
          <StoreLocation>
            <LazyLoad height={200} offset={100}>
              <StoreImage src={north} alt="Tonawanda ReStore" />
            </LazyLoad>
            <h3 className="project-title">Tonawanda ReStore</h3>
            <p>
              1030 Niagara Falls Blvd, Tonawanda, NY 14150
              <br />
              Phone: (716) 204-0740 ext. 203
              <br />
              <hr />
              <strong>Hours of Operation:</strong>
              <br />
              Tuesday - Saturday 10:00am to 7:00pm
              <br />
              Closed Sunday and Monday
            </p>
            <ButtonContainer>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=1030+Niagara+Falls+Blvd,+Tonawanda,+NY+14150"
                target="_blank"
                rel="noopener noreferrer"
                className="button secondary"
              >
                GET DIRECTIONS
              </a>
            </ButtonContainer>
          </StoreLocation>
        </StoreHoursSection>

        {/* New Section: Donation Info */}
        <Section>
          <h2 className="section-header green">How to Donate to the ReStore</h2>

          <Paragraph>
            Donating items to the Habitat Buffalo ReStore is easy and helps us
            build homes for families in need. You can drop off items at our
            South Buffalo or Tonawanda locations, or schedule a pickup for large
            donations.
            <br />
            <a href="/donate-items" target="_blank" rel="noopener noreferrer">
              Learn more about the items we accept and do not accept.
            </a>
          </Paragraph>

          {/* Donation Buttons */}
          <CardContainer>
            {/* First Row: Individual & Organization Donations */}
            <Card>
              <CardTitle>For Individuals</CardTitle>
              <CardDescription>
                If you are looking to make a one-time, personal donation to our
                ReStores.
              </CardDescription>
              <Button href="https://onlinedonationpickup.com/habitatbuffalo/#intro">
                Donate Items
              </Button>
            </Card>
            <Card>
              <CardTitle>For Organizations</CardTitle>
              <CardDescription>
                Organizations can donate large quantities of items to our
                ReStores.
              </CardDescription>
              <Button href="/form/donation">Donate as an Organization</Button>
            </Card>
          </CardContainer>
          <PremierPickupCard>
            <NewBadge>NEW</NewBadge>
            <CardTitle>Premier Pickup Service</CardTitle>
            <CardDescription>
              <strong>Premium Paid Pickup Service</strong> – In partnership with{" "}
              <strong>ReSupply</strong>, our Premier Pickup Service offers a
              faster, full-service donation pickup experience. Most pickups are
              scheduled within a few days, and items can be collected from any
              floor in your home. This service is available even outside our
              normal pickup area and accepts all items, including those we
              typically cannot take. Every pickup supports Habitat Buffalo’s
              mission to build homes for families in need.
            </CardDescription>
            <PremierButton
              href="https://donor.resupplyapp.com/start/charity/114?utm_source=widget"
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule a Pickup
            </PremierButton>
          </PremierPickupCard>
        </Section>
        {/* ReStore eBay Store CTA Section */}
        <CTASection
          title="ReStore eBay Store"
          description="Visit our online ReStore for a curated selection of unique items, collectibles, and all sorts of treasures. Your purchases help support Habitat Buffalo's mission to build homes for families in need."
          buttonText="VIEW STORE"
          buttonLink="https://www.ebay.com/str/habitatforhumanitybuffalo"
        />
        <DynamicEvents category="restore" categoryText="ReStore" />
      </Container>
    </>
  );
};

// Styled components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Paragraph = styled.p`
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 20px;
  text-align: center;
`;

const SectionHeader = styled.div`
  text-align: center;
  font-size: 1.2em;
  margin: 20px 0;
  padding: 10px;
  background-color: ${(props) =>
    props.className.includes("orange") ? "#ff671f" : "#00acd8"};
  color: #fff;
  border-radius: 8px;
`;

const StoreHoursSection = styled.div`
  text-align: center;
  padding: 20px 0;
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
`;

const StoreLocation = styled.div`
  flex: 1 1 45%;
  max-width: 600px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;

  img {
    width: 100%;
    border-radius: 8px;
    margin-bottom: 15px;
  }

  p {
    text-align: left;
  }

  .button.primary {
    display: inline-block;
    padding: 10px 20px;
    background-color: var(--hhb-green);
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-weight: bold;
    text-transform: uppercase;
    margin-top: 10px;
  }

  .button.primary:hover {
    background-color: var(--hhb-primary);
  }

  .button.secondary {
    display: inline-block;
    padding: 10px 20px;
    background-color: var(--hhb-primary);
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-weight: bold;
    text-transform: uppercase;
    margin-top: 10px;
  }

  .button.secondary:hover {
    background-color: var(--hhb-darkblue);
  }
`;

const StoreImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Section = styled.div`
  margin-top: 40px;
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 40px;
`;

const Card = styled.div`
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  width: 300px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 1.5em;
  margin-bottom: 10px;
`;

const CardDescription = styled.p`
  font-size: 1em;
  margin-bottom: 20px;
`;

const Button = styled.a`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 20px 0;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: var(--accent-color);
  }
`;

const PremierPickupCard = styled.div`
  background-color: #ffffff; /* White background */
  border: 2px solid #ff9900;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 0 20px rgba(255, 153, 0, 0.7); /* Glow effect */
  margin-top: 20px;
  position: relative;
  animation: glow 1.5s infinite alternate;

  @keyframes glow {
    from {
      box-shadow: 0 0 15px rgba(255, 153, 0, 0.5);
    }
    to {
      box-shadow: 0 0 25px rgba(255, 153, 0, 1);
    }
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  background-color: #ff0000;
  color: white;
  font-size: 16px;
  font-weight: bold;
  padding: 6px 12px;
  border-radius: 50px;
  transform: rotate(-10deg);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
`;

const PremierButton = styled.a`
  background-color: #ff9900;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 5px;
  margin-top: 15px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  transition: background 0.3s ease;

  &:hover {
    background-color: #e68a00;
  }
`;

export default ReStore;
